import cx from 'classnames';
import SVG from 'react-inlinesvg';

import Button, { Color } from 'lib/Button';
import { MultipleSwitchBoxOptionValue, MultipleSwitchBoxProps } from './MultipleSwitchBox.types';
import { MultipleSwitchBoxTextWrap, MultipleSwitchBoxTheme } from './MultipleSwitchBox.theme';

const MultipleSwitchBox = <T extends MultipleSwitchBoxOptionValue>({
  source,
  onChange = () => undefined,
  selectedValue = undefined,
  dataTestId = '',
  textKey = 'name',
  valueKey = 'code',
  icon = null,
  theme = MultipleSwitchBoxTheme.LIGHT,
  isDisabled = false,
  isFullWidth = false,
  hideBorder = false,
  wrap = MultipleSwitchBoxTextWrap.DEFAULT,
}: MultipleSwitchBoxProps<T>): React.ReactElement => {
  const isDark = theme === MultipleSwitchBoxTheme.DARK;

  return (
    <div
      className={cx('flex flex-row justify-evenly', {
        readOnly: isDisabled,
        'w-full': isFullWidth,
      })}
      data-test-id={dataTestId}
    >
      <div
        className={cx('flex items-center justify-evenly rounded-[6px]', {
          'bg-neutral-950-opacity-5 text-neutral-500': !isDark,
          'bg-primary-800 text-neutral-100 border border-primary-800': isDark,
          'border-neutral-300': !hideBorder,
          'border-none': hideBorder,
          'w-full': isFullWidth,
          'w-min': !isFullWidth,
        })}
      >
        {icon && (
          <SVG
            src={icon}
            className={cx('w-3.5 h-3.5 ml-2 mr-2', {
              '[&_path]:fill-neutral-600': !isDark,
              '[&_path]:fill-neutral-100': isDark,
              'opacity-40': isDisabled,
            })}
          />
        )}
        {source.map((option) => {
          const isSelectedOption = option[valueKey] === selectedValue;
          return (
            <span
              className={cx('text-neutral-900', wrap, {
                active: isSelectedOption,
                'inline-block w-1/2': isFullWidth,
              })}
              key={option[valueKey] as string}
              data-test-id={`multiple-switch-box-option-${option[valueKey]}`}
            >
              <Button
                onClick={() => {
                  if (!isSelectedOption && !isDisabled) onChange(option[valueKey] as T);
                }}
                color={isSelectedOption ? Color.PRIMARY_LIGHT_ACTIVE : Color.PRIMARY_LIGHT}
                isDisabled={isDisabled}
                dataTestId={`${dataTestId}-${(option[textKey] as string).replace(/\s+/g, '').toLowerCase()}`}
                svg={option.icon as string}
                svgClassnames={cx('!w-3.5 !h-3.5', {
                  '[&_path]:fill-neutral-950': !isDark && isSelectedOption,
                  '[&_path]:fill-neutral-100': isDark,
                  '[&_path]:fill-neutral-900': !isDark && !isSelectedOption,
                })}
                classNames={cx('py-[9px] px-[15px] focus:opacity-100 focus-visible:opacity-70', {
                  'w-full': isFullWidth,
                  'disabled:!bg-transparent disabled:!text-neutral-100-opacity-40': isDark,
                })}
              >
                <span
                  className={cx('body-base', {
                    'inline-block w-1/2': isFullWidth,
                  })}
                >
                  {option[textKey]}
                </span>
              </Button>
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default MultipleSwitchBox;
